import { useState, useEffect } from "react";
import { useAuth } from "@/context/auth";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, Zap, BarChart3, ShoppingCart, Package, ArrowRight } from "lucide-react";
import { BUSINESS_TYPES, type BusinessType } from "@/lib/business-types";

const STORAGE_KEY = "retailpulse_welcomed_v1";

export function WelcomeModal() {
  const { user, store } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || !store) return;
    const key = `${STORAGE_KEY}_${user.id}`;
    if (!localStorage.getItem(key)) {
      setOpen(true);
    }
  }, [user, store]);

  const handleClose = () => {
    if (user) {
      localStorage.setItem(`${STORAGE_KEY}_${user.id}`, "1");
    }
    setOpen(false);
  };

  if (!user || !store) return null;

  const btConfig = user.businessType ? BUSINESS_TYPES[user.businessType as BusinessType] : null;

  const features = [
    { icon: ShoppingCart, text: "Full POS — ring up sales in seconds" },
    { icon: Package, text: "Inventory tracking with low-stock alerts" },
    { icon: BarChart3, text: "P&L reports, revenue & expense analytics" },
    { icon: Zap, text: "Staff management & purchase orders" },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-x-hidden overflow-y-auto p-0 gap-0">
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-900 to-primary px-8 pt-8 pb-6 text-center">
          <div className="text-4xl mb-3">{btConfig?.emoji ?? "🛒"}</div>
          <DialogTitle className="text-2xl font-bold text-white mb-1">
            Welcome, {user.name.split(" ")[0]}!
          </DialogTitle>
          <DialogDescription className="text-indigo-200 text-sm">
            <strong className="text-white break-words">{store.name}</strong> is live on RetailPulse
          </DialogDescription>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <Zap className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">10-day free trial started</p>
              <p className="text-xs text-amber-700">No credit card needed. Full access to everything.</p>
            </div>
          </div>

          <div className="space-y-2.5">
            <p className="text-sm font-semibold text-foreground">Everything you need to run your store:</p>
            {features.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground">{text}</span>
              </div>
            ))}
          </div>

          <div className="bg-muted/50 rounded-xl px-4 py-3 space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Your login details</p>
            <div className="flex flex-wrap items-start justify-between gap-x-2 text-sm">
              <span className="text-muted-foreground">Email</span>
              <span className="min-w-0 break-all text-right font-mono font-medium text-foreground">{user.email}</span>
            </div>
            <p className="text-xs text-muted-foreground">Keep your password safe — you can change it in Settings.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 pb-7 pt-1 flex flex-col gap-2">
          <Button className="w-full gap-2" onClick={handleClose}>
            Let's get started <ArrowRight className="w-4 h-4" />
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Questions? Reach us at <span className="font-medium">support@retailpulsepos.com</span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
